<script setup lang="ts">
import {
  defineEmits,
  ref,
  onMounted,
  onBeforeUnmount,
  defineProps,
  defineOptions,
  PropType,
  computed,
} from "vue";
import GenericCard from "./ui/GenericCard.vue";
import { ServiceItem, personBaseData, ServiceLink } from "../utils/interfaces";
import GenericButton from "./ui/GenericButton.vue";
import { events, type EventsChannel } from "aws-amplify/api";
import { useGeneralStore } from "../stores/general";
import ViewPDF from "./ui/ViewPDF.vue";
import { getCookie } from "../utils/cookies";
import { scrollToInput, convertToMiliseconds } from "../utils/common";

defineOptions({ name: "ShowServices" });

const generalStore = useGeneralStore();
const props = defineProps({
  personData: {
    type: Object as PropType<personBaseData>,
    required: true,
  },
});
const emits = defineEmits([
  "change-view",
  "resume-timer",
  "pause-timer",
  "set-timeout",
]);

const studiesChannelPath = `studies/${props.personData.idPersona}`;
const eventsChannel = ref<EventsChannel | null>(null);
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const MAX_RECONNECT_DELAY_MS = 30000;

const servicesList = ref([] as ServiceItem[]);
const servicesUsed = ref([] as string[]);
const currentService = ref({} as ServiceLink);
const currentServiceIndex = ref(0);
const wantApplyCurrentStudy = ref(false);
const resultados = ref([] as any[]);
const showFinalMessage = ref(false);
const loadingService = ref(false);
const email = ref(props.personData.email || "");
const loadingSendResults = ref(false);
const needEmail = ref(!props.personData.email);
const canSendEmail = ref(true);
const loadingContinue = ref(false);
const idEvent = ref();
const extraDataEvent = ref({});

const getServiceValue = (service: ServiceItem, key: string) => {
  const value = service?.[key as keyof ServiceItem];
  if (!value) return "";
  if (typeof value === "string") {
    return value.replace(/.*- /g, "");
  }
  return value;
};

function changeService(serviceUsed?: string, omit?: boolean) {
  if (!serviceUsed) return;
  loadingContinue.value = true;
  servicesUsed.value.push(serviceUsed);
  wantApplyCurrentStudy.value = false;
  if (omit) {
    resultados.value.push({
      key: currentService.value.key,
      name: currentService.value.name,
      archivo: null,
    });
  }
  currentServiceIndex.value++;
  loadingContinue.value = false;
  setIdleTime();
  if (servicesList.value.length === currentServiceIndex.value) {
    if (showFinalMessage.value === true) {
      endConsultation();
      return;
    }
    emits("pause-timer");
    showFinalMessage.value = true;
    return;
  }
  if (servicesList.value[currentServiceIndex.value].usoObligatorio) {
    handleApplyCurrentStudy();
  } else if (currentService.value.url) {
    currentService.value = {} as ServiceLink;
    return;
  }
}

async function handleApplyCurrentStudy() {
  if (currentService.value.url) {
    console.log("Reutilizando servicio...");
    wantApplyCurrentStudy.value = true;
    return;
  }
  loadingService.value = true;
  const currentServiceId = servicesList.value[currentServiceIndex.value].id;
  const booth = getCookie("booth") as string;
  const idCabina = generalStore.booth.id;
  const serviceLink = (await generalStore.getServiceLink({
    idServicio: currentServiceId,
    numeroPlanAsociado: props.personData.numeroPlanAsociado,
    referencia: props.personData.referencia,
    claveCabina: encodeURIComponent(booth),
    nroOrden: props.personData.nroOrden,
    idCabina: idCabina,
  })) as any;
  if (!serviceLink.url) {
    console.log("Algo salió mal...");
    loadingService.value = false;
    return;
  }
  currentService.value = serviceLink;
  wantApplyCurrentStudy.value = true;
  loadingService.value = false;
}

const currentServiceIsFinished = computed(() => {
  return resultados.value.some(
    (result) => result.key === currentService.value.key
  );
});

// eslint-disable-next-line @typescript-eslint/no-inferrable-types
function sortByArray(array: any[], arrayToSort: string[], key: string = "id") {
  const arraySorted = array.sort((a, b) => {
    const indexA = arrayToSort.indexOf(a[key]);
    const indexB = arrayToSort.indexOf(b[key]);

    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
  return arraySorted;
}

function isEmailValid(email: string) {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

function sendResults() {
  loadingSendResults.value = true;
  if (!isEmailValid(email.value)) {
    loadingSendResults.value = false;
    return;
  }
  const cardioRiskResultLink = resultados.value.find(
    (result) => result.key === "dd1a4c2e-148a-43ed-ad54-6ac9a998c72e"
  );
  if (cardioRiskResultLink) {
    generalStore
      .sendResults(
        cardioRiskResultLink.idCardioRisk,
        email.value
      )
      .then(() => {
        loadingSendResults.value = false;
        canSendEmail.value = false;
      });
  } else {
    loadingSendResults.value = false;
  }
}

async function endConsultation() {
  loadingContinue.value = true;
  await generalStore.endConsultation({
    numeroPlanAsociado: props.personData.numeroPlanAsociado,
    referencia: props.personData.referencia,
    idCabina: generalStore.booth.id,
  });
  loadingContinue.value = false;
  emits("change-view", "baseInformation");
}

async function createEvent(extraData: any) {
  const data = {
    idCabina: generalStore.booth.id,
    idSesion: generalStore.booth.idSesion,
    numeroPlanAsociado: props.personData.numeroPlanAsociado,
    referencia: props.personData.referencia,
    ...extraData,
  };
  extraDataEvent.value = { ...extraDataEvent.value, ...extraData };
  if (idEvent.value) data.id = idEvent.value;
  const res = (await generalStore.postEvent(data)) as any;
  idEvent.value = res.id;
}

function setIdleTime() {
  const customConfig = generalStore.getBooth.customConfig;
  const currentServiceId = servicesList.value[currentServiceIndex.value]?.id;
  const currentTimer = customConfig?.idleTimers?.[currentServiceId];
  if (currentTimer && currentTimer.time) {
    const time = convertToMiliseconds(currentTimer.time, currentTimer.unit);
    emits("set-timeout", time);
    emits("resume-timer");
  }
}

function handleStudyFinishedEvent(data: any) {
  if (!data.isFinished) return;
  resultados.value.push({
    key: currentService.value.key,
    name: currentService.value.name,
    archivo: data.archivo as any,
    idCardioRisk: data.idCardioRisk,
  });
  if (currentService.value.key === "1b026971-bcc4-4399-9a78-6ed1dbd76fe5") {
    currentService.value.url = "";
    changeService(currentService.value.key);
    generalStore.getConsultas(props.personData.idPersona).then((res: any) => {
      if (res.length > 0) {
        createEvent({
          ...extraDataEvent.value,
          idConsultaMedica: res[0].id,
        });
      }
    });
  } else {
    createEvent({
      ...extraDataEvent.value,
      cardioRisk: data.archivo?.location ? true : false,
      idCardioRisk: data.idCardioRisk ?? null,
    });
  }
}

async function connectEventsChannel() {
  try {
    const channel = await events.connect(studiesChannelPath);
    eventsChannel.value = channel;
    reconnectAttempts = 0;
    channel.subscribe({
      next: ({ event }: any) => handleStudyFinishedEvent(event),
      error: (err: any) => {
        console.error("[AppSync Events] error de suscripción", err);
        scheduleReconnect();
      },
    });
  } catch (err) {
    console.error("[AppSync Events] fallo al conectar", err);
    scheduleReconnect();
  }
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  reconnectAttempts += 1;
  const delay = Math.min(1000 * 2 ** reconnectAttempts, MAX_RECONNECT_DELAY_MS);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    eventsChannel.value?.close();
    connectEventsChannel();
  }, delay);
}

onMounted(() => {
  const coberturas = generalStore.getBooth.coberturas;
  servicesList.value = sortByArray(
    coberturas,
    ["dd1a4c2e-148a-43ed-ad54-6ac9a998c72e"],
    "id"
  );
  if (servicesList.value.length === 0) {
    showFinalMessage.value = true;
    return;
  }
  if (servicesList.value[0].usoObligatorio) {
    handleApplyCurrentStudy();
  }
  connectEventsChannel();
  setIdleTime();
});

function headerContinueBtn(key: string) {
  if (key && !showFinalMessage.value) changeService(key);
  else endConsultation();
}

onBeforeUnmount(() => {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  eventsChannel.value?.close();
});
</script>

<template>
  <div class="flex flex-col items-center justify-center w-full h-full">
    <div
      v-show="resultados.length > 0"
      class="flex items-center justify-between w-full absolute top-0 py-2 px-4 border-b border-gray-200"
    >
      <p>Resultados</p>
      <ul class="flex gap-2 items-center">
        <li v-for="result in resultados" :key="result.key">
          <ViewPDF
            v-if="result.archivo?.location"
            :textBtn="`Ver ${result.name}`"
            :title="result.name"
            :url="result.archivo.location"
          />
        </li>
        <li>
          <GenericButton
            v-show="currentServiceIsFinished"
            variant="primary"
            :loading="loadingContinue"
            @click="() => headerContinueBtn(currentService?.key)"
          >
            Continuar
          </GenericButton>
        </li>
      </ul>
    </div>
    <div v-if="showFinalMessage">
      <p class="text-center text-2xl">Se han completado todos los estudios</p>
      <p class="text-center text-lg">Gracias por tu participación</p>
      <hr class="my-4 border-gray-200 w-full" />
      <div
        v-if="resultados.filter((result: any) => result.archivo?.location).length > 0"
        class="flex flex-col items-center gap-4"
      >
        <p>¿Deseas visualizar algún resultado?</p>
        <ViewPDF
          v-for="result in resultados.filter((result: any) => result.archivo?.location)"
          :key="result.key"
          :textBtn="`Ver ${result.name}`"
          :title="result.name"
          :url="result.archivo.location"
        />
        <hr class="mb-4 border-gray-200 w-full" />
        <input
          v-if="needEmail"
          @focus="scrollToInput"
          type="text"
          v-model="email"
          class="w-full border border-gray-300 rounded p-2"
          placeholder="Email"
        />
        <GenericButton
          variant="primary"
          :loading="loadingSendResults"
          @click="sendResults"
          :disabled="!canSendEmail"
        >
          {{
            canSendEmail
              ? "Enviar resultados por correo"
              : "Ya se envio el correo"
          }}
        </GenericButton>
        <hr class="mb-4 border-gray-200 w-full" />
      </div>
      <GenericButton
        @click="endConsultation"
        :loading="loadingContinue"
        class="w-full"
      >
        Finalizar
      </GenericButton>
    </div>
    <GenericCard
      v-else-if="!wantApplyCurrentStudy"
      class="max-w-1/2"
      :title="`¿Se quiere aplicar '${getServiceValue(
        servicesList[currentServiceIndex],
        'nombre'
      )}'?`"
    >
      <p
        v-if="getServiceValue(servicesList[currentServiceIndex], 'descripcion')"
        class="text-center my-5 mx-10"
      >
        {{ getServiceValue(servicesList[currentServiceIndex], "descripcion") }}
      </p>
      <div
        :class="{
          'grid grid-cols-2 gap-4': !getServiceValue(
            servicesList[currentServiceIndex],
            'usoObligatorio'
          ),
          'flex flex-col': getServiceValue(
            servicesList[currentServiceIndex],
            'usoObligatorio'
          ),
        }"
      >
        <GenericButton
          v-if="
            !getServiceValue(
              servicesList[currentServiceIndex],
              'usoObligatorio'
            )
          "
          variant="secondary"
          @click="changeService(servicesList[currentServiceIndex].id, true)"
        >
          Omitir
        </GenericButton>
        <GenericButton
          :loading="loadingService"
          @click="handleApplyCurrentStudy"
        >
          {{
            getServiceValue(servicesList[currentServiceIndex], "usoObligatorio")
              ? "Sí"
              : "Aplicar"
          }}
        </GenericButton>
      </div>
    </GenericCard>
    <iframe
      v-if="wantApplyCurrentStudy && currentService.url"
      :src="`${currentService.url}&lang=es`"
      allow="cross-origin-isolated; camera; microphone; geolocation;"
      referrerpolicy="no-referrer"
      credentialless
      allowfullscreen
      :class="
        wantApplyCurrentStudy && currentService.key === currentService.key
          ? 'w-[calc(100%-4rem)] h-[calc(100%-7rem)] mt-10'
          : 'w-0 h-0 mt-0'
      "
    >
    </iframe>
    <GenericButton
      v-if="
        wantApplyCurrentStudy &&
        currentService.url &&
        !getServiceValue(servicesList[currentServiceIndex], 'usoObligatorio')
      "
      @click="wantApplyCurrentStudy = false"
      class="absolute top-1 left-28"
      variant="error"
    >
      Cancelar
    </GenericButton>
  </div>
</template>

<style scoped>
.anchor-btn {
  display: flex;
  justify-content: center;
  height: 36px;
  background: #03a9f4;
  padding: 0 8px;
  text-align: center;
  border-radius: 4px;
  color: white;
  line-height: 36px;
  text-decoration: none;
}
</style>
